import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { NeurologicalFormField } from './formField'

/**
 * Neurological subsection adapter
 * Handles neurological assessment including PHQ2 scoring and seizure management (15 fields)
 */
export class NeurologicalAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Headache assessment
    const headaches = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES
    )?.at(0)

    const headacheType = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE
    )

    // Seizure assessment
    const seizures = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES
    )?.at(0)

    const seizureDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE
    )?.at(0)

    const seizureType = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE
    )

    const seizurePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS
    )

    // General neurological status
    const neuroStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS
    )

    const alteredAffect = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT
    )?.at(0)

    const alteredAffectFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS
    )

    // PHQ2 Depression Screening (Patient Health Questionnaire-2)
    const phq2Screening = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING
    )?.at(0)

    const phq2Notes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES
    )?.at(0)

    // PHQ2 individual questions (scored 0-3 each)
    const interest = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST
    )?.at(0)

    const depressed = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED
    )?.at(0)

    // Caregiver assessment
    const caregiverDifficulty = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [NeurologicalFormField.NEURO_HEADACHES]: headaches,
      [NeurologicalFormField.NEURO_HEADACHE_TYPE]: headacheType,
      [NeurologicalFormField.NEURO_SEIZURES]: seizures,
      [NeurologicalFormField.NEURO_SEIZURE_DATE]: seizureDate,
      [NeurologicalFormField.NEURO_SEIZURE_TYPE]: seizureType,
      [NeurologicalFormField.NEURO_SEIZURE_PRECAUTIONS]: seizurePrecautions,
      [NeurologicalFormField.NEURO_STATUS]: neuroStatus,
      [NeurologicalFormField.NEURO_ALTERED_AFFECT]: alteredAffect,
      [NeurologicalFormField.NEURO_ALTERED_AFFECT_FACTORS]: alteredAffectFactors,
      [NeurologicalFormField.NEURO_PHQ2_SCREENING]: phq2Screening,
      [NeurologicalFormField.NEURO_PHQ2_NOTES]: phq2Notes,
      [NeurologicalFormField.NEURO_INTEREST]: interest,
      [NeurologicalFormField.NEURO_DEPRESSED]: depressed,
      [NeurologicalFormField.NEURO_CAREGIVER_DIFFICULTY]: caregiverDifficulty,
      [NeurologicalFormField.NEURO_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES,
        value: this.getFormDataField(NeurologicalFormField.NEURO_HEADACHES)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES,
        value: this.getFormDataField(NeurologicalFormField.NEURO_SEIZURES)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS,
        value: this.getFormDataField(NeurologicalFormField.NEURO_STATUS)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT,
        value: this.getFormDataField(NeurologicalFormField.NEURO_ALTERED_AFFECT)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY,
        value: this.getFormDataField(NeurologicalFormField.NEURO_SYSTEM_SUMMARY)
      }
    ]

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_PHQ2_SCREENING)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING,
        value: this.getFormDataField(NeurologicalFormField.NEURO_PHQ2_SCREENING)
      })
    }

    // Conditionally add PHQ2-related fields if they exist (they are conditionally rendered)
    if (this.formDataFieldExists(NeurologicalFormField.NEURO_SEIZURE_DATE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE,
        value: this.getFormDataField(NeurologicalFormField.NEURO_SEIZURE_DATE)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_SEIZURE_TYPE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE,
        value: this.getFormDataField(NeurologicalFormField.NEURO_SEIZURE_TYPE)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_SEIZURE_PRECAUTIONS)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS,
        value: this.getFormDataField(NeurologicalFormField.NEURO_SEIZURE_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_INTEREST)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST,
        value: this.getFormDataField(NeurologicalFormField.NEURO_INTEREST)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_DEPRESSED)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED,
        value: this.getFormDataField(NeurologicalFormField.NEURO_DEPRESSED)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_CAREGIVER_DIFFICULTY)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY,
        value: this.getFormDataField(NeurologicalFormField.NEURO_CAREGIVER_DIFFICULTY)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_PHQ2_NOTES)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES,
        value: this.getFormDataField(NeurologicalFormField.NEURO_PHQ2_NOTES)
      })
    }

    if (this.formDataFieldExists(NeurologicalFormField.NEURO_ALTERED_AFFECT_FACTORS)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS,
        value: this.getFormDataField(NeurologicalFormField.NEURO_ALTERED_AFFECT_FACTORS)
      })
    }
    if (this.formDataFieldExists(NeurologicalFormField.NEURO_HEADACHE_TYPE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE,
        value: this.getFormDataField(NeurologicalFormField.NEURO_HEADACHE_TYPE)
      })
    }

    return mutations
  }
}
